import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { SyncIndicator } from './SyncIndicator';
import { SyncIssuesDrawer } from '@/components/sync/SyncIssues';
import { useUiStore } from '@/stores/uiStore';
import { startSync } from '@/lib/sync';

const TITLES: Record<string, string> = {
  '/': 'Management Dashboard',
  '/diary': 'Daily Diary',
  '/machines': 'Fleet Register',
  '/production': 'Production & Hauling',
  '/blasts': 'Blast Register',
  '/breakdowns': 'Breakdowns & Weather',
  '/maintenance': 'Maintenance & Wear Parts',
  '/workshop': 'Workshop',
  '/company': 'Company Fleet',
  '/parts': 'Parts & Stock',
  '/targets': 'Production Targets',
  '/reports': 'Reports',
  '/admin': 'Administration',
  '/help': 'Help & Guides',
  '/concrete': 'Suremix Concrete',
  '/brick': 'Brick Plant',
  '/precast': 'Pre-cast',
  '/fleet': 'Fleet',
  '/fuel': 'Fuel Management',
};

/** Sun and moon drawn as outlines in the text colour, so the switch reads
 *  on both themes (the ☾ / ☀ glyphs came out hair-thin on Android). */
function ThemeIcon({ theme }: { theme: 'light' | 'dark' }) {
  return theme === 'dark' ? (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7Z" />
    </svg>
  );
}

const FLEET_TITLES: Record<string, string> = {
  checks: 'Weekly Vehicle Checks',
  branch: 'Branch Fleet',
  breakdowns: 'Breakdowns',
};

const CONCRETE_TITLES: Record<string, string> = {
  deliveries: 'Concrete Deliveries',
  targets: 'Concrete Targets',
  lists: 'Mixes, Customers & Reasons',
  settings: 'Branch Settings & Daily Email',
};

export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const collapsed = useUiStore((s) => s.navCollapsed);
  const toggleNav = useUiStore((s) => s.toggleNav);

  // The menu button opens the drawer on a phone or tablet, and folds the
  // menu to an icon rail on a desktop.
  function menuButton() {
    if (window.matchMedia('(max-width: 900px)').matches) setNavOpen((v) => !v);
    else toggleNav();
  }

  useEffect(() => startSync(), []);
  // The drawer closes on Escape, as well as on a tap outside it.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const [, top, sub] = pathname.split('/');
  const title = (top === 'concrete' && sub && CONCRETE_TITLES[sub])
    || (top === 'fleet' && sub && FLEET_TITLES[sub])
    || TITLES[`/${top}`] || TITLES['/'] || 'Suremix';

  return (
    <div className={`shell${collapsed ? ' is-collapsed' : ''}`}>
      <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
      {navOpen && (
        <div className="shell__scrim" aria-hidden onClick={() => setNavOpen(false)} />
      )}
      <div className="shell__main">
        <header className="topbar">
          <button
            className="btn btn--ghost topbar__icon"
            onClick={menuButton}
            aria-label="Toggle navigation"
            title="Show or hide the menu"
          >
            ☰
          </button>
          <span className="topbar__title" title={title}>{title}</span>
          <span className="spacer" />
          <SyncIndicator />
          <button
            className="btn btn--ghost topbar__icon topbar__theme"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
            aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
          >
            <ThemeIcon theme={theme === 'dark' ? 'dark' : 'light'} />
          </button>
        </header>
        <main className="shell__content">
          <Outlet />
        </main>
        <SyncIssuesDrawer />
      </div>
    </div>
  );
}
